import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Home from './pages/home';
import About from './pages/common-components/About/About';
import Contact from './pages/common-components/Contact/Contact';
import Blog from './pages/common-components/Blog/Blog';
import BlogView from './pages/common-components/Blog/BlogView';
import Shop from './pages/common-components/Shop/Shop';
import SellYourCar from './pages/common-components/SellYourCar/SellYourCar';
import { CompanyProvider } from './pages/Contexts/CompanyContext';
import { CompanyProfileProvider, useCompanyProfile } from './pages/Contexts/CompanyProfileContext';
import { CategoryProvider } from './pages/Contexts/CategoryContext';
import { BrandProvider } from './pages/Contexts/BrandContext';
import { AuthProvider } from './pages/Contexts/AuthContext';
import Login from './pages/common-components/Auth/Login';
import Signup from './pages/common-components/Auth/Signup';
import ForgotPassword from './pages/common-components/Auth/ForgotPassword';
import Account from './pages/common-components/Auth/Account';
import AIChatWidget from './pages/common-components/AIChat/AIChatWidget';
import { AppLoadingProvider, useAppLoading } from './pages/Contexts/AppLoadingContext';
import AppLoader from './pages/common-components/App-Loader/AppLoader';
import ProductDetails from './pages/common-components/Product-details/ProductDetails';
import ScrollToTop from './pages/common-components/Back-to-top/ScrollToTop';
import FAQs from './pages/common-components/Faqs/Faqs';
import TestDrive from './pages/common-components/TestDrive/TestDrive';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './pages/Lib/queryClient';

// ── Logo imported once here, passed as a prop to AppLoader ───────────────────
import logoImage from './logo.png';
import TermsAndConditions from './pages/common-components/Terms-and-Conditions/Termsandconditions';
import PrivacyPolicy from './pages/common-components/Privacy-Policy/Privacypolicy';
// Update this path to match your actual logo file, e.g.:
//   import logoImage from './assets/logo.svg';
//   import logoImage from './assets/images/brand-logo.png';
// ─────────────────────────────────────────────────────────────────────────────

// ── Watches CompanyProfile loading and hides the loader when ready ────────────

const AppReadyGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading } = useCompanyProfile();
  const { setAppLoading } = useAppLoading();

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setAppLoading(false), 400);
      return () => clearTimeout(timer);
    }
  }, [loading, setAppLoading]);

  return <>{children}</>;
};

// ── Loader overlay — logo and company name passed in as props ─────────────────

const LoaderOverlay: React.FC = () => {
  const { isAppLoading } = useAppLoading();
  const { companyProfile } = useCompanyProfile();

  if (!isAppLoading) return null;

  return (
    <AppLoader
      logoImage={logoImage}
      companyName={companyProfile?.companyName}
    />
  );
};

// ── App ───────────────────────────────────────────────────────────────────────

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <CompanyProvider>
        <CompanyProfileProvider>
          <CategoryProvider>
            <BrandProvider>
              <AppLoadingProvider>
                <AppReadyGate>

                  <LoaderOverlay />

                  <Router>
                    <ScrollToTop />
                    <AuthProvider>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/shop" element={<Shop />} />
                        <Route path="/sell-your-car" element={<SellYourCar />} />
                        <Route path="/blog/:slug" element={<BlogView />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/test-drive" element={<TestDrive />} />
                        <Route path="/product/:id" element={<ProductDetails />} />
                        <Route path="/faqs" element={<FAQs />} />
                        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/account" element={<Account />} />
                      </Routes>
                      <AIChatWidget />
                    </AuthProvider>
                  </Router>

                </AppReadyGate>
              </AppLoadingProvider>
            </BrandProvider>
          </CategoryProvider>
        </CompanyProfileProvider>
      </CompanyProvider>
    </QueryClientProvider>
  );
}

export default App;